//components/Navbar/FormBuilderToolbar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Menu, MenuItem, Dialog, Box } from '@mui/material';
import { useFormContext } from '../../contexts/FormContext';
import menuStyles from '../../styles/menuStyles'; // Import menu styles

import ShareForm from '../Share/ShareForm'; 
import { useFieldEdit } from '../../contexts/FieldEditContext'; 
import { useFormListsDraftSync } from '../../contexts/FormListsDraftSyncContext';

import useDuplicateForm from '../../hooks/useDuplicateForm';
import DuplicateSuccessModal from '../Dialogs/DuplicateSuccessModal';
import { useAuth } from '../../contexts/AuthContext';

function FormBuilderToolbar() {
  const [activeStep, setActiveStep] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showShareForm, setShowShareForm] = useState(false);
  const [isStepNavigationPending, setIsStepNavigationPending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { formId, formDetails } = useFormContext();
  const { formId: urlFormId } = useParams();

  // NEW: We'll call clearSelectedField() to force the side panel closed in FormListsPage
  const { clearSelectedField } = useFieldEdit();
  const { flushFormListsDraftChanges } = useFormListsDraftSync() || {};

  const {
    handleDuplicateForm,
    duplicateModalState,
    goToDuplicatedForm,
    dismissDuplicateModal,
  } = useDuplicateForm();

  const { hasPermission } = useAuth() || {};
  const canDuplicate = Boolean(hasPermission?.('createForms'));

   const effectiveFormId = urlFormId || formId;

 const routeAUF = location.state?.isAssetUploadForm === true;
 const docAUF   = formDetails?.isAssetUploadForm === true;
 const [isDamForm, setIsDamForm] = useState(Boolean(routeAUF || docAUF));
 useEffect(() => {
   if (docAUF && !isDamForm) setIsDamForm(true);
   // do not flip back to false
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [docAUF]);



  useEffect(() => {
    if (!effectiveFormId) {
      console.error("Form ID is not available.");
    } else {
      const path = location.pathname.split('/').pop();
      switch (path) {
        case 'design':
          setActiveStep(1);
          break;
        case 'layout':
          setActiveStep(2);
          break;

        /**
         * Lists V2 transition
         * ---------------------------------------------------------------------
         * /lists is the production route.
         * /lists-lab remains a temporary alias during rollout.
         *
         * IMPORTANT
         *  • Both routes should highlight the same toolbar step.
         *  • Navigation from the toolbar should continue going to /lists.
         *  • After QA, the /lists-lab alias can be removed from routes/index.js.
         */
        case 'lists':
        case 'lists-lab':
          setActiveStep(3);
          break;

        case 'submit-options':
          setActiveStep(4);
          break;
        case 'settings':
          setActiveStep(5);
          break;
        default:
          setActiveStep(1);
          break;
      }
    }
  }, [location, effectiveFormId]);

  // Our "steps" map:
  // step 1 -> 'design'
  // step 2 -> 'layout'
  // step 3 -> 'lists'
  // step 4 -> 'submit-options'
  // step 5 -> 'settings'
  //
  // We'll close any open side panel if the user leaves step 2, 3, or 4.
  // (Feel free to customize if you want a different set of steps to close.)
  const handleStepClick = async (newStep) => {
    if (isStepNavigationPending) {
      return;
    }

    const paths = ['design', 'layout', 'lists', 'submit-options', 'settings'];
    const newPath = paths[newStep - 1];
    const currentPath = location.pathname.split('/').pop();

    if (!effectiveFormId) {
      console.error("No form ID found for navigation.");
      return;
    }

    if (!newPath) {
      console.error('[FormBuilderToolbar] Invalid builder step requested.', {
        newStep,
        paths,
      });
      return;
    }

    const isCurrentlyOnLists =
      currentPath === 'lists' ||
      currentPath === 'lists-lab';

    const isTargetLists =
      newPath === 'lists' ||
      newPath === 'lists-lab';

    const isNavigatingAwayFromLists =
      isCurrentlyOnLists &&
      !isTargetLists;

    setIsStepNavigationPending(true);

    try {
      /**
       * Form Lists pre-navigation flush
       * -----------------------------------------------------------------------
       * Controlled builder-step navigation should flush Form Lists before the
       * Lists page unmounts.
       *
       * WHY
       *  • FormListsLabPage route-exit cleanup is still useful as a fallback,
       *    but the toolbar is the controlled transition point for builder steps.
       *  • Flushing here keeps the canvas mounted while the registered Form
       *    Lists flusher can still read the active edit, dirty snapshot, and
       *    dirty option writes.
       *
       * IMPORTANT
       *  • This does NOT save to Firestore.
       *  • This does NOT publish.
       *  • This only promotes dirty Form Lists spreadsheet edits into the local
       *    builder working copy through the existing registered flusher.
       */
      if (
        isNavigatingAwayFromLists &&
        typeof flushFormListsDraftChanges === 'function'
      ) {
        const flushResult = await flushFormListsDraftChanges({
          formId: effectiveFormId,
          reason: 'builder_toolbar_leave_lists',
        });

        console.log('[FormBuilderToolbar] Form Lists pre-navigation flush result:', {
          formId: effectiveFormId,
          from: currentPath,
          to: newPath,
          flushStatus: flushResult?.status,
          flushReason: flushResult?.reason,
          resultCount: Array.isArray(flushResult?.results)
            ? flushResult.results.length
            : 0,
        });

        if (flushResult?.status === 'error') {
          console.error(
            '[FormBuilderToolbar] Navigation blocked because Form Lists ' +
              'pre-navigation flush failed.',
            {
              formId: effectiveFormId,
              from: currentPath,
              to: newPath,
              flushResult,
            }
          );

          return;
        }
      }

      setActiveStep(newStep);

      // Steps that must close the side panel when the user leaves them
      // (2 = Layout, 3 = Lists, 4 = Submit Options)
      const stepsRequiringCloseOnLeave = [2, 3, 4];

      // If our *current* step is in that list, and we're navigating to a different step,
      // we forcibly clear the selected field to ensure the panel closes.
      if (stepsRequiringCloseOnLeave.includes(activeStep) && newStep !== activeStep) {
        console.log(`[FormBuilderToolbar] Leaving step ${activeStep}. Clearing selected field to close side panel.`);
        clearSelectedField();
      }

      navigate(`/form-builder/${effectiveFormId}/${newPath}`);
    } finally {
      setIsStepNavigationPending(false);
    }
  };

  const handlePreview = () => {
    if (effectiveFormId) {
      const previewUrl = `${window.location.origin}/form-builder/${effectiveFormId}/preview`;
      window.open(previewUrl, '_blank');
    } else {
      alert("No form ID available for preview.");
    }
  };

  const handleViewSubmissions = () => {
    if (effectiveFormId) {
      navigate(`/forms/${effectiveFormId}/submissions`);
    } else {
      alert("No form ID available for submissions.");
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (option) => {
    handleMenuClose();
    switch(option) {
      case 'Preview':
        handlePreview();
        break;
      case 'Share':
        setShowShareForm(true);
        break;
      case 'Submissions':
        handleViewSubmissions();
        break;

      // Temporarily hidden in the menu (keep for later)
      // case 'Duplicate':
      //   if (!canDuplicate) {
      //     console.warn('[FormBuilderToolbar] Duplicate blocked: no permission');
      //     break;
      //   }
      //   if (effectiveFormId) {
      //     handleDuplicateForm(effectiveFormId);
      //   } else {
      //     alert("No form ID available to duplicate.");
      //   }
      //   break;

      // case 'Delete':
      //   // Implement navigation or action for Delete
      //   break;

      default:
        break;
    }

  };


  const steps = [
    { label: 'Design', step: 1 },
    { label: 'Layout', step: 2 },
    { label: 'Lists', step: 3 },
    // Hide “Submit Options” for Asset Upload Form
    ...(isDamForm ? [] : [{ label: 'Submit Options', step: 4 }]),
    { label: 'Settings', step: 5 },
  ];

  return (
    <div style={styles.toolbar}>
      <div style={styles.stepsContainer}>
        {steps.map(({ label, step }) => (
          <span
            key={step}
            onClick={() => handleStepClick(step)}
            onMouseEnter={(e) => { if (!isStepNavigationPending && activeStep !== step) e.target.style.color = '#ffffff'; }}
            onMouseLeave={(e) => { if (!isStepNavigationPending && activeStep !== step) e.target.style.color = '#9e9e9e'; }}
            style={{
              ...styles.step,
              fontWeight: activeStep === step ? 'bold' : 'normal',
              color: activeStep === step ? '#ffffff' : '#9e9e9e',
              pointerEvents: isStepNavigationPending ? 'none' : 'auto',
              opacity: isStepNavigationPending && activeStep !== step ? 0.65 : 1,
            }}
          >
            {label}
          </span>
        ))}
      </div>
      <Button
        variant="outlined"
        onClick={handleMenuOpen}
        endIcon={<ArrowDropDownIcon />}
        style={styles.actionsButton}
      >
        Actions
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        MenuListProps={{ dense: true }}
        PaperProps={{ style: menuStyles.menuPaper }} // Use menu styles

//        PaperProps={{
//          style: {
//            width: '200px',
//            boxShadow: 'none',
//            border: '1px solid #D3E0EA',
//            marginTop: '6px',
//          },
//        }}
      >
        {[
          'Preview',
          'Share',
          'Submissions',
          // Temporarily hide Duplicate + Delete in builder toolbar actions
          // ...(isDamForm ? [] : ['Duplicate']),
          // 'Delete',
        ].map(option => (
          <MenuItem
            key={option}
            onClick={() => handleMenuClick(option)}
            style={styles.menuItem}
            disabled={option === 'Duplicate' && !canDuplicate}
          >
            {option}
          </MenuItem>
        ))}
      </Menu>


      <Dialog
        open={showShareForm}
        onClose={() => setShowShareForm(false)}
        fullWidth
        maxWidth="sm"
      >
        <Box sx={{ p: 3 }}>
          <ShareForm
            formName={formDetails?.title || 'Untitled Form'}
            formId={effectiveFormId}
            formUrl={
              formDetails?.status === 'published'
                ? `/forms/${effectiveFormId}`
                : null
            }
            formStatus={formDetails?.status || 'draft'}
            isPublic={formDetails?.isPublic}
            orgAccess={formDetails?.orgAccess}
            sharedWith={formDetails?.sharedWith}
            onClose={() => setShowShareForm(false)}
          />
        </Box>
      </Dialog>

      {/* Duplicate Success Modal (same pattern as HomeCard) */}
      <DuplicateSuccessModal
        open={duplicateModalState.open}
        onClose={dismissDuplicateModal}
        onNavigate={goToDuplicatedForm}
      />
    </div>
  );
}


const styles = {
  toolbar: {
    backgroundColor: '#0A2E4F',
    height: '48px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 16px',
  },
  stepsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  step: {
    cursor: 'pointer',
    marginRight: '40px',
    fontSize: '14px',
  },
  actionsButton: {
    color: '#ffffff',
    borderColor: '#ffffff',
    borderRadius: '6px',
    textTransform: 'none',
    fontSize: '11px',
    padding: '4px 8px',
  },
  menuPaper: {
    width: '200px',
    boxShadow: 'none',
    border: '1px solid #D3E0EA',
    marginTop: '8px',
  },
  menuItem: {
    fontSize: '12px',
  },
};

export default FormBuilderToolbar;
